import React, { useState, useEffect, useRef } from 'react';
import {
    MessageSquare, Volume2, Info, PhoneIncoming, PhoneOutgoing, PhoneMissed,
    User, ArrowRight, Edit3, Zap, Tag, Clock, ChevronDown, ChevronUp, Send,
} from 'lucide-react';
import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { optimizedApi } from '@/utils/api/optimized-api';
import { showToast } from '@/components/ui/Toast';

// ─── Types ───────────────────────────────────────────────────────────────────

interface TimelineEvent {
    event_type: 'remark' | 'activity' | 'disposition' | 'call';
    event_id: string;
    occurred_at: string | null;
    subtype: string;
    actor_name: string | null;
    content: string | null;
    old_value: string | null;
    new_value: string | null;
    meta: any | null;
}

interface LeadTimelineProps {
    leadUuid: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const safeParseDate = (v: string | null): Date | null => {
    if (!v) return null;
    try {
        const d = parseISO(v);
        return isNaN(d.getTime()) ? null : d;
    } catch { return null; }
};

const formatTime = (v: string | null): string => {
    const d = safeParseDate(v);
    if (!d) return '';
    if (isToday(d)) return format(d, 'HH:mm');
    if (isYesterday(d)) return `Yesterday · ${format(d, 'HH:mm')}`;
    if (d.getFullYear() === new Date().getFullYear()) return format(d, 'MMM d · HH:mm');
    return format(d, 'MMM d yyyy · HH:mm');
};

const formatDateLabel = (v: string | null): string => {
    const d = safeParseDate(v);
    if (!d) return '';
    if (isToday(d)) return 'Today';
    if (isYesterday(d)) return 'Yesterday';
    if (d.getFullYear() === new Date().getFullYear()) return format(d, 'EEEE, MMMM d');
    return format(d, 'MMMM d, yyyy');
};

const formatDuration = (secs: string | null): string => {
    if (!secs) return '';
    const n = parseInt(secs, 10);
    if (isNaN(n) || n <= 0) return '';
    const m = Math.floor(n / 60);
    const s = n % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// ─── Event type config ────────────────────────────────────────────────────────

interface EventConfig {
    dotBg: string;         // dot + icon background
    dotRing: string;       // ring around dot
    icon: React.ReactNode;
    label: string;
    cardBorder: string;
    cardBg: string;
    yearColor: string;     // accent color for actor name
}

const getConfig = (evt: TimelineEvent): EventConfig => {
    const ic = (C: React.ElementType, cls: string) => <C className={`h-4 w-4 ${cls}`} strokeWidth={2} />;

    if (evt.event_type === 'remark') {
        switch (evt.subtype) {
            case 'audio':
                return { dotBg: 'bg-blue-500', dotRing: 'ring-blue-200', icon: ic(Volume2, 'text-white'), label: 'Audio note', cardBorder: 'border-blue-200', cardBg: 'bg-blue-50', yearColor: 'text-blue-600' };
            case 'system':
                return { dotBg: 'bg-gray-400', dotRing: 'ring-gray-200', icon: ic(Info, 'text-white'), label: 'System', cardBorder: 'border-gray-200', cardBg: 'bg-gray-50', yearColor: 'text-gray-500' };
            case 'call':
                return { dotBg: 'bg-teal-500', dotRing: 'ring-teal-200', icon: ic(PhoneIncoming, 'text-white'), label: 'Call note', cardBorder: 'border-teal-200', cardBg: 'bg-teal-50', yearColor: 'text-teal-600' };
            default:
                return { dotBg: 'bg-amber-400', dotRing: 'ring-amber-200', icon: ic(MessageSquare, 'text-white'), label: 'Note', cardBorder: 'border-amber-200', cardBg: 'bg-amber-50', yearColor: 'text-amber-600' };
        }
    }

    if (evt.event_type === 'disposition') {
        return { dotBg: 'bg-indigo-500', dotRing: 'ring-indigo-200', icon: ic(Tag, 'text-white'), label: 'Disposition', cardBorder: 'border-indigo-200', cardBg: 'bg-indigo-50', yearColor: 'text-indigo-600' };
    }

    if (evt.event_type === 'call') {
        const missed = evt.content === 'missed' || evt.subtype === 'missed';
        const inbound = evt.subtype === 'inbound';
        if (missed) return { dotBg: 'bg-red-400', dotRing: 'ring-red-200', icon: ic(PhoneMissed, 'text-white'), label: 'Missed call', cardBorder: 'border-red-200', cardBg: 'bg-red-50', yearColor: 'text-red-500' };
        if (inbound) return { dotBg: 'bg-teal-500', dotRing: 'ring-teal-200', icon: ic(PhoneIncoming, 'text-white'), label: 'Incoming call', cardBorder: 'border-teal-200', cardBg: 'bg-teal-50', yearColor: 'text-teal-600' };
        return { dotBg: 'bg-green-500', dotRing: 'ring-green-200', icon: ic(PhoneOutgoing, 'text-white'), label: 'Outgoing call', cardBorder: 'border-green-200', cardBg: 'bg-green-50', yearColor: 'text-green-600' };
    }

    // activity subtypes
    switch (evt.subtype) {
        case 'assignment':
            return { dotBg: 'bg-purple-500', dotRing: 'ring-purple-200', icon: ic(User, 'text-white'), label: 'Assignment', cardBorder: 'border-purple-200', cardBg: 'bg-purple-50', yearColor: 'text-purple-600' };
        case 'status_change':
            return { dotBg: 'bg-brandColor', dotRing: 'ring-brandColor/20', icon: ic(ArrowRight, 'text-white'), label: 'Status change', cardBorder: 'border-blue-200', cardBg: 'bg-blue-50', yearColor: 'text-brandColor' };
        case 'field_update':
            return { dotBg: 'bg-emerald-500', dotRing: 'ring-emerald-200', icon: ic(Edit3, 'text-white'), label: 'Info updated', cardBorder: 'border-emerald-200', cardBg: 'bg-emerald-50', yearColor: 'text-emerald-600' };
        default:
            return { dotBg: 'bg-amber-500', dotRing: 'ring-amber-200', icon: ic(Zap, 'text-white'), label: 'Activity', cardBorder: 'border-amber-200', cardBg: 'bg-orange-50', yearColor: 'text-amber-600' };
    }
};

// ─── Event card body ──────────────────────────────────────────────────────────

const EventBody: React.FC<{ evt: TimelineEvent }> = ({ evt }) => {
    const [expanded, setExpanded] = useState(false);

    if (evt.event_type === 'remark') {
        if (evt.subtype === 'audio') {
            return (
                <audio controls className="w-full h-8 mt-2" preload="metadata">
                    <source src={evt.content || ''} type="audio/mpeg" />
                    <source src={evt.content || ''} type="audio/wav" />
                </audio>
            );
        }
        const text = evt.content || '';
        const isLong = text.length > 180;
        return (
            <div className="mt-1.5">
                <p className={`text-sm text-gray-700 whitespace-pre-wrap break-words leading-relaxed ${!expanded && isLong ? 'line-clamp-3' : ''}`}>{text}</p>
                {isLong && (
                    <button onClick={() => setExpanded(v => !v)} className="flex items-center gap-1 text-xs text-brandColor mt-1 hover:underline">
                        {expanded ? <><ChevronUp className="h-3 w-3" />Show less</> : <><ChevronDown className="h-3 w-3" />Show more</>}
                    </button>
                )}
            </div>
        );
    }

    if (evt.event_type === 'disposition') {
        return (
            <div className="flex flex-wrap items-center gap-2 mt-2">
                {evt.old_value && <span className="px-2.5 py-1 bg-white text-gray-600 rounded-full text-xs border border-gray-300">{evt.old_value}</span>}
                {evt.old_value && evt.new_value && <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />}
                {evt.new_value && <span className="px-2.5 py-1 bg-white text-indigo-700 rounded-full text-xs border border-indigo-300 font-medium">{evt.new_value}</span>}
            </div>
        );
    }

    if (evt.event_type === 'call') {
        const statusLabel = evt.content ? evt.content.charAt(0).toUpperCase() + evt.content.slice(1) : 'Unknown';
        const duration = formatDuration(evt.old_value);
        const recording = evt.new_value;
        const statusColor = evt.content === 'answered'
            ? 'bg-green-100 text-green-700 border-green-200'
            : evt.content === 'missed' ? 'bg-red-100 text-red-700 border-red-200'
            : 'bg-gray-100 text-gray-600 border-gray-200';
        return (
            <div className="flex flex-wrap items-center gap-2 mt-2">
                <span className={`px-2.5 py-0.5 rounded-full text-xs border font-medium ${statusColor}`}>{statusLabel}</span>
                {duration && (
                    <span className="flex items-center gap-1 text-xs text-gray-500">
                        <Clock className="h-3 w-3" />{duration}
                    </span>
                )}
                {recording && (
                    <a href={recording} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1 text-xs text-brandColor hover:underline font-medium">
                        <Volume2 className="h-3 w-3" />Recording
                    </a>
                )}
            </div>
        );
    }

    // activity
    if (evt.subtype === 'assignment' && evt.meta) {
        const prev = evt.meta.previous_owner;
        const next = evt.meta.assigned_to_user;
        const by = evt.meta.assigned_by;
        return (
            <div className="flex flex-wrap items-center gap-2 mt-2">
                {prev && <span className="px-2.5 py-1 bg-white text-gray-700 rounded-full text-xs border border-gray-300">{prev}</span>}
                {prev && next && <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />}
                {next && <span className="px-2.5 py-1 bg-white text-purple-700 rounded-full text-xs border border-purple-300 font-medium">{next}</span>}
                {!prev && !next && <span className="text-xs text-gray-500">Unassigned</span>}
                {by && <span className="text-xs text-gray-400">by {by}</span>}
            </div>
        );
    }

    if (evt.subtype === 'status_change' && evt.meta) {
        const oldS = evt.meta.old_status?.name;
        const newS = evt.meta.new_status?.name;
        if (!oldS && !newS) return null;
        return (
            <div className="flex flex-wrap items-center gap-2 mt-2">
                {oldS && <span className="px-2.5 py-1 bg-white text-gray-600 rounded-full text-xs border border-gray-300">{oldS}</span>}
                {oldS && newS && <ArrowRight className="h-3 w-3 text-gray-400 flex-shrink-0" />}
                {newS && <span className="px-2.5 py-1 bg-white text-green-700 rounded-full text-xs border border-green-300 font-medium">{newS}</span>}
            </div>
        );
    }

    if (evt.subtype === 'field_update' && evt.meta?.changes?.length) {
        const changes: string[] = (evt.meta.changes as string[]).filter(c => !c.includes('Status changed from'));
        if (!changes.length) return null;
        return (
            <div className="mt-2 space-y-1">
                {changes.map((c, i) => (
                    <p key={i} className="text-xs text-gray-600 bg-white border border-gray-100 rounded px-2 py-1">{c}</p>
                ))}
            </div>
        );
    }

    // generic (lead_created, lead_qualified, etc.)
    const label = evt.content?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return label ? <p className="text-xs text-gray-500 mt-1.5">{label}</p> : null;
};

// ─── Single timeline row ──────────────────────────────────────────────────────

const TimelineRow: React.FC<{ evt: TimelineEvent; isLast: boolean }> = ({ evt, isLast }) => {
    const cfg = getConfig(evt);
    const time = formatTime(evt.occurred_at);
    const actor = evt.actor_name || 'System';

    return (
        <div className="flex gap-3">
            {/* Spine column: dot + vertical line */}
            <div className="flex flex-col items-center flex-shrink-0 w-8">
                <div className={`w-8 h-8 rounded-full ${cfg.dotBg} ring-4 ${cfg.dotRing} flex items-center justify-center flex-shrink-0 shadow-sm`}>
                    {cfg.icon}
                </div>
                {!isLast && <div className="w-0.5 flex-1 bg-gray-200 mt-1 mb-0" />}
            </div>

            {/* Card */}
            <div className={`flex-1 mb-4 rounded-xl border ${cfg.cardBorder} ${cfg.cardBg} p-3 shadow-sm min-w-0`}>
                {/* Header row */}
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <span className={`text-xs font-semibold uppercase tracking-wide ${cfg.yearColor}`}>{cfg.label}</span>
                        <p className="text-sm font-medium text-gray-800 truncate mt-0.5">{actor}</p>
                    </div>
                    {time && <span className="text-xs text-gray-400 flex-shrink-0 whitespace-nowrap mt-0.5">{time}</span>}
                </div>
                {/* Body */}
                <EventBody evt={evt} />
            </div>
        </div>
    );
};

// ─── Date section header ──────────────────────────────────────────────────────

const DateSection: React.FC<{ label: string }> = ({ label }) => (
    <div className="flex gap-3 mb-2">
        {/* Spine alignment: empty dot-width space */}
        <div className="w-8 flex-shrink-0 flex justify-center">
            <div className="w-0.5 bg-gray-200 h-full" />
        </div>
        <div className="flex-1 pb-1">
            <span className="inline-block text-xs font-bold text-gray-400 uppercase tracking-widest bg-white px-2 py-0.5 rounded-full border border-gray-200">
                {label}
            </span>
        </div>
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const LeadTimeline: React.FC<LeadTimelineProps> = ({ leadUuid }) => {
    const [events, setEvents] = useState<TimelineEvent[]>([]);
    const [loading, setLoading] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (!leadUuid) return;
        let active = true;
        const fetch = async () => {
            setLoading(true);
            try {
                const res = await optimizedApi.get<TimelineEvent[]>(`/leads/${leadUuid}/timeline`);
                if (active && res.success) setEvents(res.data || []);
            } catch { /* ignore */ } finally {
                if (active) setLoading(false);
            }
        };
        fetch();
        return () => { active = false; };
    }, [leadUuid]);

    const handleAddNote = async () => {
        const text = noteText.trim();
        if (!text || submitting) return;
        setSubmitting(true);
        try {
            const res = await optimizedApi.post<any>('/leads/remarks', { lead_uuid: leadUuid, remark: text });
            if (res.success && res.data) {
                setEvents(prev => [{
                    event_type: 'remark',
                    event_id: res.data.remarkUuid || `tmp-${Date.now()}`,
                    occurred_at: res.data.date || new Date().toISOString(),
                    subtype: 'normal',
                    actor_name: res.data.userDisplay || 'You',
                    content: text,
                    old_value: null, new_value: null, meta: null,
                }, ...prev]);
                setNoteText('');
                showToast('Note added', 'success');
            } else {
                showToast(res.message || 'Failed to add note', 'error');
            }
        } catch { showToast('Failed to add note', 'error'); }
        finally { setSubmitting(false); }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleAddNote(); }
    };

    // Group events by date label
    type Group = { label: string; events: TimelineEvent[] };
    const groups: Group[] = [];
    let lastLabel = '';
    for (const evt of events) {
        const label = formatDateLabel(evt.occurred_at);
        if (label !== lastLabel) { groups.push({ label, events: [] }); lastLabel = label; }
        groups[groups.length - 1].events.push(evt);
    }

    // Flat list with date separators for isLast calculation
    const flatRows: Array<{ evt: TimelineEvent; isLast: boolean; dateLabel?: string }> = [];
    for (let gi = 0; gi < groups.length; gi++) {
        const grp = groups[gi];
        for (let ei = 0; ei < grp.events.length; ei++) {
            const isLastRow = gi === groups.length - 1 && ei === grp.events.length - 1;
            flatRows.push({ evt: grp.events[ei], isLast: isLastRow, dateLabel: ei === 0 ? grp.label : undefined });
        }
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            {/* Add note area — sticky at top */}
            <div className="px-4 pt-4 pb-3 border-b bg-white flex-shrink-0">
                <div className="relative">
                    <textarea
                        ref={textareaRef}
                        value={noteText}
                        onChange={e => setNoteText(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Add a note… (Ctrl+Enter to save)"
                        rows={2}
                        className="w-full pr-10 pl-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent bg-gray-50"
                    />
                    <button
                        onClick={handleAddNote}
                        disabled={!noteText.trim() || submitting}
                        className="absolute right-2.5 bottom-2.5 w-7 h-7 bg-brandColor text-white rounded-lg flex items-center justify-center hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity"
                        title="Add note"
                    >
                        <Send className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>

            {/* Timeline list */}
            <div className="flex-1 overflow-y-auto px-4 pt-5 pb-4">
                {loading ? (
                    <div className="flex items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor" />
                        <span className="ml-2 text-gray-500 text-sm">Loading timeline…</span>
                    </div>
                ) : events.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                            <Clock className="h-7 w-7 text-gray-300" />
                        </div>
                        <p className="text-gray-500 font-medium text-sm">No activity yet</p>
                        <p className="text-gray-400 text-xs mt-1">Notes, calls, and changes will appear here</p>
                    </div>
                ) : (
                    <div>
                        {flatRows.map(({ evt, isLast, dateLabel }, idx) => (
                            <React.Fragment key={`${evt.event_type}-${evt.event_id}-${idx}`}>
                                {dateLabel && <DateSection label={dateLabel} />}
                                <TimelineRow evt={evt} isLast={isLast} />
                            </React.Fragment>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LeadTimeline;
